/* Elyia — global scripts (vanilla JS, no dependencies) */
(() => {
  'use strict';

  const Elyia = (window.Elyia = window.Elyia || {});
  Elyia.routes = Elyia.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update', predictiveSearch: '/search/suggest' };
  Elyia.settings = Elyia.settings || { cartType: 'drawer', moneyFormat: '£{{amount}}' };
  Elyia.strings = Elyia.strings || {};

  /* ---------------------------------------------------------------------
     Utilities
     --------------------------------------------------------------------- */
  Elyia.debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  Elyia.formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || Elyia.settings.moneyFormat || '£{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const toFixedSep = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const n = (number / 100).toFixed(precision);
      const parts = n.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cts = parts[1] ? decimal + parts[1] : '';
      return dollars + cts;
    };
    const match = fmt.match(placeholder);
    if (!match) return fmt;
    let value = '';
    switch (match[1]) {
      case 'amount': value = toFixedSep(cents, 2); break;
      case 'amount_no_decimals': value = toFixedSep(cents, 0); break;
      case 'amount_with_comma_separator': value = toFixedSep(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = toFixedSep(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = toFixedSep(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = toFixedSep(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = toFixedSep(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = toFixedSep(cents, 2, ' ', '.'); break;
      default: value = toFixedSep(cents, 2);
    }
    // Quiet luxury: drop .00 on whole amounts (e.g. £500 rather than £500.00)
    value = value.replace(/[.,]00$/, '');
    return fmt.replace(placeholder, value).replace(/<[^>]*>/g, '');
  };

  Elyia.toast = (message) => {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => (el.hidden = true), 3500);
  };

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const trapFocus = (container, initial) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const els = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container._trap && document.removeEventListener('keydown', container._trap);
    container._trap = handler;
    document.addEventListener('keydown', handler);
    (initial || container).focus({ preventScroll: true });
  };
  const releaseFocus = (container) => {
    if (container && container._trap) document.removeEventListener('keydown', container._trap);
  };

  /* ---------------------------------------------------------------------
     Drawers (menu + cart)
     --------------------------------------------------------------------- */
  const Drawers = {
    lastTrigger: null,
    open(id, trigger) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      this.lastTrigger = trigger || document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.querySelectorAll(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const panel = drawer.querySelector('.drawer__panel');
      requestAnimationFrame(() => trapFocus(panel, panel.querySelector('[data-drawer-close].icon-button') || panel));
    },
    close(drawer) {
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      releaseFocus(drawer.querySelector('.drawer__panel'));
      if (this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus({ preventScroll: true });
    }
  };
  Elyia.drawers = Drawers;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); Drawers.open(opener.dataset.drawerOpen, opener); return; }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { e.preventDefault(); Drawers.close(closer.closest('[data-drawer]')); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-drawer].is-open').forEach((d) => Drawers.close(d));
    const search = document.querySelector('[data-search-modal].is-open');
    if (search) Search.close();
  });

  /* ---------------------------------------------------------------------
     Header
     --------------------------------------------------------------------- */
  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }
  customElements.define('header-component', HeaderComponent);

  /* ---------------------------------------------------------------------
     Search modal + predictive search
     --------------------------------------------------------------------- */
  const Search = {
    get modal() { return document.querySelector('[data-search-modal]'); },
    open(trigger) {
      const m = this.modal;
      if (!m) return false;
      this.trigger = trigger;
      m.classList.add('is-open');
      m.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      const input = m.querySelector('input[type="search"]');
      setTimeout(() => trapFocus(m.querySelector('.search-modal__panel'), input), 50);
      return true;
    },
    close() {
      const m = this.modal;
      if (!m) return;
      m.classList.remove('is-open');
      m.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      releaseFocus(m.querySelector('.search-modal__panel'));
      this.trigger && this.trigger.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-search-open]');
    if (open && Search.modal) { e.preventDefault(); Search.open(open); return; }
    if (e.target.closest('[data-search-close]')) { e.preventDefault(); Search.close(); }
  });

  const initPredictive = () => {
    const input = document.querySelector('[data-predictive-input]');
    const results = document.querySelector('[data-predictive-results]');
    if (!input || !results || input.dataset.enabled !== 'true') return;
    let controller;
    const render = (products, term) => {
      if (!term) { results.innerHTML = ''; return; }
      if (!products.length) {
        results.innerHTML = `<p class="text-muted">${(Elyia.strings.noResults || 'No results for “[terms]”').replace('[terms]', escapeHtml(term))}</p>`;
        return;
      }
      const items = products.map((p) => {
        const img = p.featured_image && p.featured_image.url
          ? `<img src="${p.featured_image.url}${p.featured_image.url.includes('?') ? '&' : '?'}width=400" alt="${escapeHtml(p.featured_image.alt || p.title)}" loading="lazy" width="400" height="400">`
          : '';
        const price = Elyia.formatMoney(Math.round(parseFloat(p.price) * 100));
        return `<a class="predictive-results__item" href="${p.url}"><span class="media">${img}</span><span>${escapeHtml(p.title)}</span><span class="text-muted">${price}</span></a>`;
      }).join('');
      results.innerHTML = `<div class="predictive-results__grid">${items}</div><a class="link-arrow predictive-results__all" href="${Elyia.routes.root.replace(/\/$/, '')}/search?q=${encodeURIComponent(term)}&type=product">${escapeHtml(input.form.querySelector('[type=submit]').getAttribute('aria-label') || 'Search')} &ldquo;${escapeHtml(term)}&rdquo;</a>`;
    };
    input.addEventListener('input', Elyia.debounce(async () => {
      const term = input.value.trim();
      if (controller) controller.abort();
      if (!term) { render([], ''); return; }
      controller = new AbortController();
      try {
        const url = `${Elyia.routes.predictiveSearch}.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();
        render((data.resources && data.resources.results && data.resources.results.products) || [], term);
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    }, 220));
  };

  const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  Elyia.escapeHtml = escapeHtml;

  /* ---------------------------------------------------------------------
     Cart
     --------------------------------------------------------------------- */
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const Cart = {
    sections() {
      const ids = ['cart-drawer'];
      document.querySelectorAll('[data-cart-section-id]').forEach((el) => ids.push(el.dataset.cartSectionId));
      return ids;
    },
    async add(formData) {
      formData.append('sections', this.sections().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${Elyia.routes.cartAdd}`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || Elyia.strings.cartError || 'Error');
      }
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${Elyia.routes.cartChange}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sections(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || Elyia.strings.cartError);
      this.render(data.sections);
      if (data.errors) Elyia.toast(data.errors);
      return data;
    },
    async updateNote(note) {
      await fetch(`${Elyia.routes.cartUpdate}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
    },
    render(sections) {
      if (!sections) return;
      if (sections['cart-drawer']) {
        const doc = parseHTML(sections['cart-drawer']);
        const fresh = doc.querySelector('[data-cart-drawer-inner]');
        const current = document.querySelector('#CartDrawer [data-cart-drawer-inner]');
        if (fresh && current) {
          current.replaceWith(fresh);
          this.updateCount(parseInt(fresh.dataset.itemCount, 10) || 0);
        }
      }
      document.querySelectorAll('[data-cart-section-id]').forEach((el) => {
        const html = sections[el.dataset.cartSectionId];
        if (!html) return;
        const fresh = parseHTML(html).querySelector('[data-cart-section-id]');
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
    },
    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.dataset.count = count;
        el.innerHTML = count > 0 ? `<span aria-hidden="true">${count}</span><span class="visually-hidden">${count}</span>` : '';
      });
    },
    open() {
      if (Elyia.settings.cartType !== 'drawer' || document.body.classList.contains('template-cart')) {
        window.location.href = Elyia.routes.cart;
        return;
      }
      Drawers.open('CartDrawer');
    }
  };
  Elyia.cart = Cart;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && Elyia.settings.cartType === 'drawer' && !document.body.classList.contains('template-cart') && document.getElementById('CartDrawer')) {
      e.preventDefault();
      Drawers.open('CartDrawer', opener);
    }
  });

  // Quantity +/- control
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) => btn.addEventListener('click', (e) => {
        e.preventDefault();
        const prev = this.input.value;
        btn.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  // Line item quantity + remove (drawer and cart page)
  const lineUpdate = async (line, quantity, itemEl) => {
    itemEl && itemEl.classList.add('is-loading');
    try {
      await Cart.change(line, quantity);
    } catch (err) {
      Elyia.toast(err.message);
      itemEl && itemEl.classList.remove('is-loading');
    }
  };

  document.addEventListener('change', Elyia.debounce((e) => {
    const input = e.target.closest('[data-cart-item] input[name="updates[]"]');
    if (!input) return;
    lineUpdate(parseInt(input.dataset.line, 10), parseInt(input.value, 10) || 0, input.closest('[data-cart-item]'));
  }, 300));

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-cart-note]')) Cart.updateNote(e.target.value);
  });

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    lineUpdate(parseInt(remove.dataset.line, 10), 0, remove.closest('[data-cart-item]'));
  });

  /* ---------------------------------------------------------------------
     Hero slideshow
     --------------------------------------------------------------------- */
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.track.children);
      this.current = 0;
      if (this.slides.length < 2) return;
      this.currentEl = this.querySelector('[data-current]');
      this.progress = this.querySelector('[data-progress]');
      this.querySelector('[data-prev]').addEventListener('click', () => { this.goTo(this.current - 1); this.restart(); });
      this.querySelector('[data-next]').addEventListener('click', () => { this.goTo(this.current + 1); this.restart(); });
      this.track.addEventListener('scroll', Elyia.debounce(() => this.sync(), 60), { passive: true });
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.autoplay = this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = (parseInt(this.dataset.speed, 10) || 6) * 1000;
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.indexOf(e.target);
          if (i > -1) { this.stop(); this.goTo(i); }
        });
      }
      this.start();
    }
    goTo(i) {
      const n = this.slides.length;
      const index = ((i % n) + n) % n;
      this.track.scrollTo({ left: this.slides[index].offsetLeft, behavior: 'smooth' });
      this.setIndex(index);
    }
    sync() {
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (index !== this.current) this.setIndex(index);
    }
    setIndex(index) {
      this.current = index;
      if (this.currentEl) this.currentEl.textContent = String(index + 1).padStart(2, '0');
      if (this.progress) this.progress.style.setProperty('--progress', (index + 1) / this.slides.length);
      this.slides.forEach((s, i) => {
        s.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        s.querySelectorAll('a, button').forEach((a) => (a.tabIndex = i === index ? 0 : -1));
      });
    }
    start() { if (this.autoplay && !this.timer) this.timer = setInterval(() => this.goTo(this.current + 1), this.speed); }
    stop() { clearInterval(this.timer); this.timer = null; }
    restart() { this.stop(); this.start(); }
    disconnectedCallback() { this.stop(); }
  }
  customElements.define('hero-slideshow', HeroSlideshow);

  /* ---------------------------------------------------------------------
     Scroll carousel (arrows for scroll-snap rows)
     --------------------------------------------------------------------- */
  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track || !this.prev || !this.next) return;
      const step = () => {
        const item = this.track.children[0];
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth;
      };
      this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        this.prev.disabled = this.track.scrollLeft <= 2;
        this.next.disabled = this.track.scrollLeft >= max;
      };
      this.track.addEventListener('scroll', Elyia.debounce(update, 50), { passive: true });
      window.addEventListener('resize', Elyia.debounce(update, 150));
      update();
    }
  }
  customElements.define('scroll-carousel', ScrollCarousel);

  /* ---------------------------------------------------------------------
     Video cards + full-screen modal
     --------------------------------------------------------------------- */
  const VideoModal = {
    get dialog() { return document.querySelector('[data-video-modal]'); },
    open(card) {
      const dialog = this.dialog;
      if (!dialog || !card.dataset.videoSrc) return;
      const video = dialog.querySelector('video');
      this.trigger = card;
      video.poster = card.dataset.poster || '';
      video.src = card.dataset.videoSrc;
      video.muted = false;
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      const p = video.play();
      if (p && p.catch) p.catch(() => { video.muted = true; video.play(); });
    },
    close() {
      const dialog = this.dialog;
      if (!dialog) return;
      const video = dialog.querySelector('video');
      video.pause();
      video.removeAttribute('src');
      video.load();
      if (dialog.open) dialog.close();
      document.body.classList.remove('overflow-hidden');
      this.trigger && this.trigger.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (e) => {
    const card = e.target.closest('.video-card[data-video-src]');
    if (card) { e.preventDefault(); VideoModal.open(card); return; }
    if (e.target.closest('[data-video-modal-close]') || e.target.matches('[data-video-modal]')) VideoModal.close();
  });

  const initVideoModal = () => {
    const dialog = VideoModal.dialog;
    if (!dialog) return;
    dialog.addEventListener('close', () => {
      const v = dialog.querySelector('video');
      if (v && !v.paused) VideoModal.close();
      document.body.classList.remove('overflow-hidden');
    });
    dialog.addEventListener('cancel', () => VideoModal.close());
  };

  const initVideoPreviews = (root = document) => {
    const cards = root.querySelectorAll('.video-card[data-autoplay]');
    if (!cards.length || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        const video = card.querySelector('video');
        if (!video) return;
        if (entry.isIntersecting) {
          if (!video.src) video.src = card.dataset.previewSrc;
          const p = video.play();
          if (p && p.then) p.then(() => card.classList.add('is-playing')).catch(() => {});
        } else {
          video.pause();
          card.classList.remove('is-playing');
        }
      });
    }, { threshold: 0.4 });
    cards.forEach((c) => io.observe(c));
  };
  Elyia.initVideoPreviews = initVideoPreviews;

  /* ---------------------------------------------------------------------
     Accordions (smooth open/close for <details data-accordion>)
     --------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('details[data-accordion] > summary');
    if (!summary) return;
    const details = summary.parentElement;
    const content = details.querySelector('[data-accordion-content]');
    if (!content || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    e.preventDefault();
    if (details._anim) details._anim.cancel();
    if (details.open) {
      const start = content.offsetHeight;
      details._anim = content.animate({ height: [`${start}px`, '0px'], opacity: [1, 0] }, { duration: 300, easing: 'cubic-bezier(.22,.61,.36,1)' });
      details._anim.onfinish = () => { details.open = false; details._anim = null; };
    } else {
      details.open = true;
      const end = content.offsetHeight;
      details._anim = content.animate({ height: ['0px', `${end}px`], opacity: [0, 1] }, { duration: 340, easing: 'cubic-bezier(.22,.61,.36,1)' });
      details._anim.onfinish = () => { details._anim = null; };
    }
  });

  /* ---------------------------------------------------------------------
     Modals (<dialog>)
     --------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.modalOpen);
      if (dialog && typeof dialog.showModal === 'function') { e.preventDefault(); dialog.showModal(); }
      return;
    }
    const closer = e.target.closest('[data-modal-close]');
    if (closer) { closer.closest('dialog').close(); return; }
    if (e.target.matches('dialog.modal')) e.target.close();
  });

  /* ---------------------------------------------------------------------
     Product recommendations
     --------------------------------------------------------------------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const html = await res.text();
          const fresh = parseHTML(html).querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
          } else {
            const section = this.closest('.section');
            if (section) section.hidden = true;
          }
        } catch (e) { console.error(e); }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { io.disconnect(); load(); }
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------------------------------------------------------------------
     Reveal on scroll
     --------------------------------------------------------------------- */
  const initReveal = () => {
    const els = document.querySelectorAll('.reveal:not(.is-revealed)');
    if (!('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  };

  /* ---------------------------------------------------------------------
     Customer addresses: province selector
     --------------------------------------------------------------------- */
  const initAddressForms = () => {
    document.querySelectorAll('[data-address-country]').forEach((countrySelect) => {
      const form = countrySelect.closest('form');
      const provinceSelect = form.querySelector('[data-address-province]');
      const provinceWrap = form.querySelector('[data-province-wrapper]');
      const defaultCountry = countrySelect.dataset.default;
      if (defaultCountry) {
        Array.from(countrySelect.options).forEach((o) => { if (o.value === defaultCountry) o.selected = true; });
      }
      const update = () => {
        const option = countrySelect.options[countrySelect.selectedIndex];
        const provinces = option ? JSON.parse(option.dataset.provinces || '[]') : [];
        provinceSelect.innerHTML = '';
        if (!provinces.length) { provinceWrap.hidden = true; return; }
        provinceWrap.hidden = false;
        provinces.forEach(([value, label]) => {
          const o = document.createElement('option');
          o.value = value; o.textContent = label;
          if (value === provinceSelect.dataset.default) o.selected = true;
          provinceSelect.appendChild(o);
        });
      };
      countrySelect.addEventListener('change', update);
      update();
    });

    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-address-toggle]');
      if (toggle) {
        const target = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!target) return;
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        target.hidden = expanded;
      }
      const del = e.target.closest('[data-address-delete]');
      if (del && window.confirm(del.dataset.confirm)) {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = del.dataset.target;
        form.innerHTML = '<input type="hidden" name="_method" value="delete">';
        document.body.appendChild(form);
        form.submit();
      }
    });
  };

  /* ---------------------------------------------------------------------
     Customer login: recover password toggle
     --------------------------------------------------------------------- */
  const initRecover = () => {
    const login = document.querySelector('[data-login-form]');
    const recover = document.querySelector('[data-recover-form]');
    if (!login || !recover) return;
    const show = (which) => { login.hidden = which !== 'login'; recover.hidden = which !== 'recover'; };
    if (window.location.hash === '#recover') show('recover');
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-show-recover], [data-show-login]');
      if (!t) return;
      e.preventDefault();
      show(t.hasAttribute('data-show-recover') ? 'recover' : 'login');
    });
  };

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  const boot = () => {
    initPredictive();
    initVideoModal();
    initVideoPreviews();
    initReveal();
    initAddressForms();
    initRecover();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  document.addEventListener('shopify:section:load', (e) => {
    initVideoPreviews(e.target);
    initReveal();
  });
})();
